import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import { Badge } from '@/components/shadcn-ui/badge';
import { Button } from '@/components/shadcn-ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/shadcn-ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/shadcn-ui/dialog';
import { Filter, Download, MoreHorizontal, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { appAxios } from '@/network/base/appAxios';
import PermitStatusBadge from '@/components/PermitStatusBadge';
import { toast } from 'sonner';
import { useUserStore } from '@/stores/useUser.store';


const ReviewPermits = () => {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
    });
    const [admins, setAdmins] = useState([]);
    const [loadingAdmins, setLoadingAdmins] = useState(false);
    const adminDetails = useUserStore(state => state.adminDetails);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingAssignment, setPendingAssignment] = useState(null);

    const getRequests = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await appAxios.get('/Admin/v1.0/admin-assigned-permits', {
                params: { page, limit },
            });

            if (response.data?.data?.data) {
                // Transform API data to match table structure
                const transformedData = response.data.data.data.map(permit => ({
                    id: permit._id,
                    customerName: permit?.user_id?.fullname || 'N/A',
                    customerEmail: permit?.user_id?.user_email || 'N/A',
                    permitId: permit.application_id || permit._id.slice(-8),
                    type: getPermitType(permit.permit_type),
                    project: permit.project_name || 'N/A',
                    address: permit.portal_address || 'N/A',
                    location: permit.portal_name || 'N/A',
                    workDescription: permit.work_description || 'N/A',
                    submitted: formatDate(permit.createdAt),
                    unit: permit.unit || 'N/A',
                    readyToFill: permit.ready_to_fill,
                    submitToCity: permit.submit_to_city,
                    automation: permit?.queue_id?.status || 'Pending',
                    permit_status: permit?.permit_status || "new",
                    permit_manual_status: permit?.permit_manual_status || "pending_zermit_fees",
                    assignedTo: permit?.assigned_to?.fullname || null,

                    // static fields 
                    invoice: "Pending"
                }));

                setRequests(transformedData);
                setPagination(response.data.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching permits:', error);
            toast.error('Failed to fetch permits', {
                description: error.response?.data?.message || error.message || 'An error occurred while fetching permits'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchAdmins = async () => {
        setLoadingAdmins(true);
        try {
            const response = await appAxios.get('/Admin/v1.0/admin-assigned-permits', {
                params: { page: 1, limit: 100 },
            });
            if (response.data?.data?.data) {
                const filteredAdmins = response.data.data.data.filter(admin => admin.user_email !== adminDetails.user_email);
                setAdmins(filteredAdmins);
            }
        } catch (error) {
            console.error('Error fetching admins:', error);
            toast.error('Failed to fetch admin list');
        } finally {
            setLoadingAdmins(false);
        }
    };

    useEffect(() => {
        getRequests(pagination.page, pagination.limit);
        fetchAdmins();
    }, []);

    const getPermitType = type => {
        const types = {
            '0': 'Bldg Alter Repair',
            '1': 'Electrical Vehicle Charger',
            '2': 'Bldg Alter Repair',
            '3': 'Fire Sprinkler',
            '4': 'HVAC',
            '5': 'Plumbing',
            '6': 'Pressure Vessel',
            '7': 'Temporary Special Event',
        };
        return types[type] || `${type}`;
    };

    const formatDate = dateString => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const handlePageChange = page => {
        getRequests(page, pagination.limit);
    };

    const getStatusBadge = status => {
        const variants = {
            Submitted: 'default',
            Ready: 'success',
            'In Progress': 'warning',
            New: 'info',
            pending: 'pending',
            running: "running",
            completed: "success",
            failed: "destructive",

        };
        return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
    };

    const handleAssignAdmin = (permitId, adminId, adminName) => {
        setPendingAssignment({ permitId, adminId, adminName });
        setConfirmDialogOpen(true);
    };

    const confirmAssignment = async () => {
        try {
            const { permitId, adminId, adminName } = pendingAssignment;
            const response = await appAxios.post(`/Admin/v1.0/assign-permit/${permitId}`, { adminId: adminId });
            if (response.status == 200) {
                toast.success(`Assigned to ${adminName}`);
            } else {
                toast.error('Failed to assign admin');
            }
            getRequests(pagination.page, pagination.limit);
        } catch (error) {
            console.error('Error assigning admin:', error);
            toast.error('Failed to assign admin');
        } finally {
            setConfirmDialogOpen(false);
            setPendingAssignment(null);
        }
    };

    const columns = [
        {
            key: 'permitId',
            label: 'Permit ID',
            cellClassName: 'font-medium',
            render: (value, row) => (
                <button
                    onClick={() => navigate(`/admin/dashboard/permits/${row.id}`)}
                    className="text-primary hover:underline font-medium cursor-pointer"
                >
                    {value}
                </button>
            ),
        },
        {
            key: 'type',
            label: 'Type',
        },
        {
            key: 'project',
            label: 'Project',
            render: (value, row) => (
                <div className="max-w-[200px]">
                    <div className="truncate font-medium" title={value}>
                        {value}
                    </div>
                    {/* <div className="text-xs text-muted-foreground truncate" title={row.workDescription}>
            {row.workDescription}
          </div> */}
                </div>
            ),
        },
        {
            key: 'customerEmail',
            label: 'Customer',
            render: (value, row) => (

                <div className="max-w-[200px]">
                    <div className="truncate font-medium" title={value}>
                        {row.customerName}
                    </div>
                    <div className="text-xs text-muted-foreground truncate" title={row.workDescription}>
                        {row.customerEmail}
                    </div>
                </div>
            ),
        },

        {
            key: 'address',
            label: 'Address',
            render: (value, row) => (
                <div className="max-w-[250px]">
                    <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <span className="truncate" title={value}>{value}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">{row.location}</div>
                </div>
            ),
        },
        // {
        //   key: 'unit',
        //   label: 'Unit',
        //   cellClassName: 'text-muted-foreground',
        // },
        {
            key: 'manual_status',
            label: 'Manual Status',
            render: (value, row) => <PermitStatusBadge status={row?.permit_manual_status || "pending_zermit_fees"} />,
        },
        {
            key: 'status',
            label: 'Status',
            render: (value, row) => <PermitStatusBadge status={row?.permit_status || "new"} />,
        },
        {
            key: 'automation',
            label: 'Automation',
            render: value => getStatusBadge(value),
        },
        {
            key: 'submitted',
            label: 'Submitted',
            cellClassName: 'text-muted-foreground',
        },
        // {
        //   key: 'actions',
        //   label: '',
        //   render: (_, row) => (
        //     <DropdownMenu>
        //       <DropdownMenuTrigger asChild>
        //         <Button variant="ghost" size="icon" className="h-8 w-8">
        //           <MoreHorizontal className="h-4 w-4" />
        //         </Button>
        //       </DropdownMenuTrigger>
        //       <DropdownMenuContent align="end">
        //         <DropdownMenuItem>View Details</DropdownMenuItem>
        //         <DropdownMenuItem>Edit</DropdownMenuItem>
        //         <DropdownMenuItem>Download</DropdownMenuItem>
        //         <DropdownMenuItem className="text-destructive">Delete</DropdownMenuItem>
        //       </DropdownMenuContent>
        //     </DropdownMenu>
        //   ),
        // },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Review Permits</h1>
                    <p className="text-muted-foreground">
                        Review and take action on permits assigned to you.
                    </p>

                </div>
                {/* <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div> */}
            </div>

            {/* Data Table */}
            <DataTable
                data={requests}
                columns={columns}
                searchable={false}
                loading={loading}
                pagination={pagination}
                onPageChange={handlePageChange}
                emptyMessage="No permit requests found"
                className="whitespace-nowrap"

            />

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Assignment</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Are you sure you want to assign this permit to <strong>{pendingAssignment?.adminName}</strong>?
                    </p>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={confirmAssignment}>
                            Confirm
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ReviewPermits;
